import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { func, number, bool, string, shape, oneOf } from 'prop-types';

import last from 'lodash.last';
import formatDate from 'helpers/formatDate';
import formatNumber from 'helpers/formatNumber';

import getTimeAxisConfiguration from './helpers/getTimeAxisConfiguration';
import useMiddleY from './hooks/useMiddleY';

import periodPropType from './prop-types/period';
import {
  primaryTypography,
  numbersTypography,
} from 'styles/global/typography/typography';

import { Label } from './shared.style';
import { definitionLabel } from 'styles/base-classes';
import useMedia from 'hooks/useMedia';
import entitiesPropType from './prop-types/entities';
import first from 'lodash.first';

const TickText = styled(Label)`
  text-transform: none;
`;

const YTickText = styled(TickText)`
  ${numbersTypography};
`;

const XTickText = styled(TickText)`
  ${primaryTypography};
`;

const AxisLabel = styled(Label)`
  ${definitionLabel};
  font-weight: 600;
  transform: ${({ isRotated }) => isRotated && 'rotate(-90deg)'};
`;

const Axis = styled('g')`
  path,
  line {
    stroke: ${({ theme }) => theme.Grey};
  }
`;

export const HorizontalAxis = styled(Axis)`
  g:last-of-type:not(:only-of-type) ${XTickText} {
    text-anchor: end;
  }
`;

export const VerticalAxis = styled(Axis)`
  overflow: hidden;
  display: ${({ isHidden }) => (isHidden ? 'none' : 'block')};

  path,
  line {
    stroke: currentColor;
    stroke-dasharray: 1, 5;
  }
`;

export const AxisBaseLine = styled('line')`
  stroke: ${({ theme }) => theme.Grey};
`;

export const VerticalAxe = (props) => {
  const {
    dataType,
    hasNoData,
    isPositionLabel,
    isSmallScreen,
    labels,
    margin,
    svgLeftPadding,
    showVerticalAxisLabels,
    sizes,
    verticalAxesLabel,
    verticalLabelX,
    verticalLabelY,
    xOffset,
    xAxisOffset = 0,
    y,
    yOffset,
    yTicks,
    yAxisOffset,
    isCutGrid,
    setLabelHeight,
  } = props;

  const verticalAxesRef = useRef(null);

  useEffect(() => {
    if (verticalAxesRef && verticalAxesRef.current && setLabelHeight) {
      setLabelHeight(verticalAxesRef.current.getBoundingClientRect().height);
    }
  }, [verticalAxesRef, setLabelHeight]);

  /** Intend between numbers and the y axis */
  const yTextXIntend = isCutGrid ? 5 : 0;

  return (
    <VerticalAxis
      transform={`translate(0, ${margin.top})`}
      isHidden={showVerticalAxisLabels && hasNoData}
    >
      {verticalAxesLabel && (
        <AxisLabel
          x={verticalLabelX}
          y={verticalLabelY}
          dy={yOffset}
          isRotated={isPositionLabel}
          ref={verticalAxesRef}
        >
          {verticalAxesLabel}
        </AxisLabel>
      )}
      {yTicks.map((tick, i) => (
        <g
          key={`${labels.y}-tick-${tick}`}
          transform={`translate(${xAxisOffset},${y(tick)})`}
        >
          {tick !== 0 && tick !== 1 && (
            <line
              stroke="currentColor"
              x1={isCutGrid ? margin.left : svgLeftPadding}
              x2={sizes.width + margin.left + margin.right + svgLeftPadding}
            />
          )}

          {/*
              if this is a small screen, and this is either the baseline tick or a third tick,
              display it the tick text. If this is a larger screen, always display the tick text.
            */}
          {(!isSmallScreen || i === 0 || (i + 1) % 3 === 0) && (
            <YTickText
              textAnchor="end"
              x={margin.left - xOffset + svgLeftPadding - yTextXIntend}
              dy={yAxisOffset}
            >
              {tick < 1 && dataType === 'number'
                ? tick
                : formatNumber(tick, dataType, true)}
            </YTickText>
          )}
        </g>
      ))}
    </VerticalAxis>
  );
};

function Axes(props) {
  const {
    x,
    y,
    isSmallScreen,
    period,
    numberOfDataPoints,
    margin,
    sizes,
    labels,
    hasNoData,
    showVerticalAxisLabels,
    dataType,
    hasComplementary,
    additionalLabel,
    isLabelRotated,
    yAxisOffset,
    isCutGrid,
    trends,
  } = props;

  const isMobile = useMedia('(max-width: 40em)');

  const [labelHeight, setLabelHeight] = useState(null);

  // Default offset for the labels
  const yOffset = -3;
  const xOffset = 24;

  // x-axis configuration
  const timePeriod = useCallback(
    getTimeAxisConfiguration({
      period,
      isSmallScreen,
      numberOfDataPoints,
    }),
    [period, isSmallScreen, numberOfDataPoints]
  );

  const isPosition = dataType === 'position';
  const yTicks = isPosition ? y.ticks(6).filter(Number.isInteger) : y.ticks(6);
  const xTicks =
    (period !== 'year' && isMobile) || numberOfDataPoints === 2 // we need only first and last dates on AXES for week, day and month views in case if mobile view or 2 dataPoints
      ? [last(trends[0].data).date, first(trends[0].data).date]
      : x.ticks(timePeriod);

  // If this is a Charts topic (and we are displaying 'positions')
  // we should manually add position #1 to the vertical ticks,
  // as both the ticks() function and the domain.ticks() function
  // chuck it out when generating a nicely spaced domain
  if (dataType === 'position') {
    yTicks.push(1);
  }

  const axisBaseLineYValue =
    dataType === 'position' ? y(last(y.domain())) : y(0);

  /** Checking to display Position label */
  const isPositionLabel =
    isPosition ||
    (dataType === 'number' &&
      labels.y &&
      labels.y.toLowerCase() === 'position');
  /** The approximate height and width of the left vertical label */
  const leftLabelRotatedHeight = 14;
  const leftLabelRotatedWidth = 60;
  const verticalLabelX = isPositionLabel
    ? -(sizes.height + margin.top) / 2 - 7
    : isLabelRotated
    ? -((sizes.height + margin.top) / 2 + leftLabelRotatedWidth)
    : margin.left;
  const verticalLabelY =
    isPositionLabel || isLabelRotated
      ? leftLabelRotatedHeight
      : y(last(yTicks));
  /** the padding to display a label on the left */
  const svgLeftPadding = isPositionLabel ? leftLabelRotatedHeight + 6 : 0;
  const yLabelText =
    y && yTicks.length > 0 && labels.y
      ? dataType === 'number' && labels.y.toLowerCase() === 'position'
        ? 'Streams'
        : labels.y
      : null;
  const additionalLabelX = isPositionLabel
    ? -(sizes.height + margin.top) / 2 - 7
    : margin.left;
  const additionalLabelY = isPositionLabel
    ? leftLabelRotatedHeight
    : y(last(yTicks));
  const calculatedMiddleY = useMiddleY({
    height: sizes.height,
    lastPointY: y(last(yTicks)),
    labelHeight,
  });

  return (
    <>
      <HorizontalAxis
        transform={`translate(${margin.left + svgLeftPadding}, ${margin.top})`}
      >
        {xTicks.map((tick) => (
          <g
            key={`datetime-tick-${tick}`}
            transform={`translate(${x(tick)},0)`}
          >
            {tick !== 0 && (
              <line
                stroke="currentColor"
                y1={y(last(yTicks))}
                y2={sizes.height}
              />
            )}

            {!hasComplementary && (
              <XTickText
                textAnchor="middle"
                x={0}
                y={sizes.height}
                dy={xOffset}
              >
                {formatDate(tick, period, isSmallScreen && !isMobile)}
              </XTickText>
            )}
          </g>
        ))}
      </HorizontalAxis>
      <VerticalAxe
        dataType={dataType}
        hasNoData={hasNoData}
        isPositionLabel={isPositionLabel || isLabelRotated}
        isSmallScreen={isSmallScreen}
        labels={labels}
        margin={margin}
        svgLeftPadding={
          isLabelRotated ? leftLabelRotatedHeight + 6 : svgLeftPadding
        }
        showVerticalAxisLabels={showVerticalAxisLabels}
        sizes={sizes}
        xOffset={xOffset}
        verticalAxesLabel={yLabelText}
        verticalLabelX={yAxisOffset ? -calculatedMiddleY : verticalLabelX}
        verticalLabelY={verticalLabelY}
        y={y}
        yOffset={yOffset}
        yTicks={yTicks}
        yAxisOffset={yAxisOffset || yOffset}
        isCutGrid={isCutGrid}
        setLabelHeight={setLabelHeight}
      />
      {additionalLabel && (
        <Axis transform={`translate(0, ${margin.top})`}>
          <AxisLabel
            x={additionalLabelX}
            y={additionalLabelY}
            dy={-15}
            isRotated={isPositionLabel}
          >
            {additionalLabel}
          </AxisLabel>
        </Axis>
      )}
      {/** Horizontal line */}
      <AxisBaseLine
        x1={isCutGrid ? leftLabelRotatedWidth : svgLeftPadding}
        x2={sizes.width + margin.left + margin.right + svgLeftPadding}
        y1={axisBaseLineYValue + margin.top}
        y2={axisBaseLineYValue + margin.top}
      />
      {/** Vertical line */}
      {isCutGrid && (
        <AxisBaseLine
          x1={isCutGrid ? leftLabelRotatedWidth : svgLeftPadding}
          x2={isCutGrid ? leftLabelRotatedWidth : svgLeftPadding}
          y1={y(last(yTicks)) + margin.top}
          y2={axisBaseLineYValue + margin.top}
        />
      )}
    </>
  );
}

Axes.defaultProps = {
  showVerticalAxisLabels: true,
};

Axes.propTypes = {
  /** A d3-scale linear time scale */
  x: func.isRequired,
  /** A d3-scale linear continuous scale */
  y: func.isRequired,
  /** Are we on mobile? */
  isSmallScreen: bool.isRequired,
  /** Day, week, month or year */
  period: periodPropType.isRequired,
  /** The margin configuration object from LineChart */
  margin: shape({
    top: number.isRequired,
    right: number.isRequired,
    bottom: number.isRequired,
    left: number.isRequired,
  }).isRequired,
  /** Used to define several sizes. Defined in SVG coordinates (ie. unitless). */
  sizes: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,
  /** The labels for the x and y axes */
  labels: shape({
    x: string,
    y: string,
  }),
  /** Do we have any values in the data to base the axes on? */
  hasNoData: bool,
  /** Option to show labels on the y axis */
  showVerticalAxisLabels: bool,
  /** What kind of data are we showing? This is 'number' by default */
  dataType: oneOf(['position', 'number']),
  /** Does this chart have a complementary chart? */
  hasComplementary: bool,
  /**
   * The number of dataPoints there are on the X-scale.
   * This can vary because we trim the charts so that they only show actual values.
   * We use this to calculate the number of ticks. */
  numberOfDataPoints: number.isRequired,
  additionalLabel: string,
  /** Used for positioning the left axis label */
  isLabelRotated: bool,
  /** Indent to align the numbers with the grid lines */
  yAxisOffset: number,
  /** SVG height */
  isCutGrid: bool,
  trends: entitiesPropType,
};

export default Axes;
